import React from 'react';
import { observer } from 'mobx-react-lite';
import {
  Box,
  Typography,
  IconButton,
  Switch,
  Slider,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { t } from '../config';

const SliderRow = ({ label, value, onChange, min = 0, max = 100, step = 1 }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Slider
        size="small"
        value={value}
        onChange={(_, v) => onChange(v)}
        min={min}
        max={max}
        step={step}
        sx={{ flex: 1 }}
      />
      <TextField
        size="small"
        type="number"
        value={typeof value === 'number' ? Math.round(value * 10) / 10 : value}
        onChange={(e) => {
          const v = parseFloat(e.target.value);
          if (!isNaN(v)) onChange(v);
        }}
        onFocus={(e) => e.target.select()}
        sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
        inputProps={{ min, max, step }}
      />
    </Box>
  </Box>
);

const TextEffectsPanel = observer(({ store }) => {
  const panel = store._toolbarPanel;
  if (!panel || panel.type !== 'effects') return null;

  const element = store.getElementById(panel.elementId);
  if (!element || element.type !== 'text') return null;

  const handleClose = () => store.closeToolbarPanel();
  const commit = () => store.history.addUndoState();

  return (
    <Box
      sx={{
        width: 280,
        height: '100%',
        overflowY: 'auto',
        overflowX: 'hidden',
        backgroundColor: '#fff',
        borderRight: '1px solid #e0e0e0',
        flexShrink: 0,
        p: 2,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {t('toolbar.textEffects', 'Text Effects')}
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Blur */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('toolbar.blur', 'Blur')}</Typography>
          <Switch
            size="small"
            checked={element.blurEnabled}
            onChange={(e) => { element.set({ blurEnabled: e.target.checked }); commit(); }}
          />
        </Box>
        {element.blurEnabled && (
          <SliderRow
            label={t('toolbar.blur', 'Blur')}
            value={element.blurRadius}
            onChange={(v) => element.set({ blurRadius: v })}
            min={0}
            max={40}
          />
        )}
      </Box>

      {/* Text Stroke */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('toolbar.textStroke', 'Text Stroke')}</Typography>
          <Switch
            size="small"
            checked={element.strokeWidth > 0}
            onChange={(e) => {
              element.set({
                strokeWidth: e.target.checked ? 2 : 0,
                stroke: element.stroke || '#000000',
              });
              commit();
            }}
          />
        </Box>
        {element.strokeWidth > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <input
              type="color"
              value={element.stroke || '#000000'}
              onChange={(e) => { element.set({ stroke: e.target.value }); commit(); }}
              style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
            />
            <TextField
              size="small"
              type="number"
              value={element.strokeWidth}
              onChange={(e) => {
                const v = parseFloat(e.target.value);
                if (!isNaN(v) && v >= 0) { element.set({ strokeWidth: v }); commit(); }
              }}
              onFocus={(e) => e.target.select()}
              sx={{ width: 65, '& .MuiInputBase-input': { fontSize: 12, py: 0.5, textAlign: 'center' } }}
              inputProps={{ min: 0, max: 20, step: 0.5 }}
            />
          </Box>
        )}
      </Box>

      {/* Background */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('toolbar.background', 'Background')}</Typography>
          <Switch
            size="small"
            checked={element.backgroundEnabled}
            onChange={(e) => { element.set({ backgroundEnabled: e.target.checked }); commit(); }}
          />
        </Box>
        {element.backgroundEnabled && (
          <>
            <SliderRow
              label={t('toolbar.cornerRadius', 'Corner radius')}
              value={element.backgroundCornerRadius}
              onChange={(v) => element.set({ backgroundCornerRadius: v })}
              min={0}
              max={50}
              step={0.5}
            />
            <SliderRow
              label={t('toolbar.padding', 'Padding')}
              value={element.backgroundPadding}
              onChange={(v) => element.set({ backgroundPadding: v })}
              min={0}
              max={100}
            />
            <SliderRow
              label={t('toolbar.opacity', 'Opacity')}
              value={Math.round(element.backgroundOpacity * 100)}
              onChange={(v) => element.set({ backgroundOpacity: v / 100 })}
              min={0}
              max={100}
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{t('toolbar.color', 'Color')}</Typography>
              <input
                type="color"
                value={element.backgroundColor || '#ffffff'}
                onChange={(e) => { element.set({ backgroundColor: e.target.value }); commit(); }}
                style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
            </Box>
          </>
        )}
      </Box>

      {/* Shadow */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>{t('toolbar.shadow', 'Shadow')}</Typography>
          <Switch
            size="small"
            checked={element.shadowEnabled}
            onChange={(e) => { element.set({ shadowEnabled: e.target.checked }); commit(); }}
          />
        </Box>
        {element.shadowEnabled && (
          <>
            <SliderRow
              label={t('toolbar.blur', 'Blur')}
              value={element.shadowBlur}
              onChange={(v) => element.set({ shadowBlur: v })}
              min={0}
              max={50}
            />
            <SliderRow
              label={t('toolbar.offsetX', 'Offset X')}
              value={element.shadowOffsetX}
              onChange={(v) => element.set({ shadowOffsetX: v })}
              min={-50}
              max={50}
            />
            <SliderRow
              label={t('toolbar.offsetY', 'Offset Y')}
              value={element.shadowOffsetY}
              onChange={(v) => element.set({ shadowOffsetY: v })}
              min={-50}
              max={50}
            />
            <SliderRow
              label={t('toolbar.opacity', 'Opacity')}
              value={Math.round(element.shadowOpacity * 100)}
              onChange={(v) => element.set({ shadowOpacity: v / 100 })}
              min={0}
              max={100}
            />
            <Box sx={{ mt: 1 }}>
              <Typography variant="caption" sx={{ color: '#888', display: 'block', mb: 0.5 }}>{t('toolbar.color', 'Color')}</Typography>
              <input
                type="color"
                value={element.shadowColor || '#000000'}
                onChange={(e) => { element.set({ shadowColor: e.target.value }); commit(); }}
                style={{ width: 32, height: 32, border: '1px solid #ccc', borderRadius: 4, cursor: 'pointer', padding: 0 }}
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
});

TextEffectsPanel.displayName = 'TextEffectsPanel';
export { TextEffectsPanel };
